import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Sparkles, ArrowLeft, LayoutTemplate, Link2 } from 'lucide-react';
import { API_URL, cn } from '../lib/utils';

function CreateSprint() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const template = location.state?.template;
  const importedContent = location.state?.importedContent;
  
  const [formData, setFormData] = useState({
    userName: '',
    niche: template?.inputs?.niche || '',
    targetAudience: template?.inputs?.targetAudience || importedContent?.targetAudience || '',
    offer: template?.inputs?.offer || '',
    tone: template?.inputs?.tone || importedContent?.recommendedTone || 'Professional',
    contentGoal: template?.inputs?.contentGoal || 'Leads',
    funnelSpeed: template?.inputs?.funnelSpeed || 'Balanced',
    voiceExamples: '',
    sourceUrl: importedContent?.sourceUrl || '',
    sourceInsights: importedContent?.keyInsights || []
  });

  const toneOptions = ['Professional', 'Bold', 'Storytelling', 'Analytical', 'Contrarian', 'Warm'];
  const goalOptions = ['Leads', 'Thought Leadership', 'Engagement', 'Newsletter'];
  const speedOptions = ['Conservative', 'Balanced', 'Aggressive'];

  const validateText = (value, fieldName) => {
    if (!value || !value.trim()) {
      return `${fieldName} is required`;
    }
    // Check if only numbers
    if (/^\d+$/.test(value.trim())) {
      return `${fieldName} cannot be only numbers`;
    }
    // Check if meaningful (at least 2 words or 10 characters)
    if (value.trim().length < 10 && value.trim().split(/\s+/).length < 2) {
      return `${fieldName} should be meaningful (at least 2 words or 10 characters)`;
    }
    return null;
  };

  const validateURL = (value) => {
    if (!value || !value.trim()) return null; // Optional field
    
    // Basic URL pattern validation
    const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
    
    if (!urlPattern.test(value.trim())) {
      return 'Please enter a valid URL (e.g., https://linkedin.com/posts/...)';
    }
    
    return null;
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      const nameError = validateText(formData.userName, 'Your Name');
      if (nameError) newErrors.userName = nameError;
      
      const nicheError = validateText(formData.niche, 'Niche');
      if (nicheError) newErrors.niche = nicheError;
    }
    
    if (stepNum === 2) {
      const audienceError = validateText(formData.targetAudience, 'Ideal Client');
      if (audienceError) newErrors.targetAudience = audienceError;
      
      const offerError = validateText(formData.offer, 'Core Offer');
      if (offerError) newErrors.offer = offerError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/sprints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ inputs: formData })
      });
      if (response.ok) {
        const sprint = await response.json();
        navigate(`/sprint/${sprint.sprint_id}`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create sprint');
      }
    } catch (error) {
      alert('Failed to create sprint');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const isStepValid = () => {
    if (step === 1) return formData.userName.trim() && formData.niche.trim();
    if (step === 2) return formData.targetAudience.trim() && formData.offer.trim();
    return true;
  };

  return (
    <div className="min-h-screen bg-main">
      <header className="border-b border-border bg-card/50 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-muted hover:text-foreground">
              <ArrowLeft className="w-5 h-5" /> Back
            </button>
          </div>
        </div>
      </header>

      {template && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
            <LayoutTemplate className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary">Using {template.name} template</span>
          </div>
        </div>
      )}

      {importedContent && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="rounded-xl bg-gradient-to-r from-primary/5 to-indigo-500/5 border border-primary/20 p-4">
            <div className="flex items-start gap-3">
              <Link2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Imported from URL</p>
                <p className="text-xs text-muted mb-2 truncate max-w-md">{importedContent.sourceUrl}</p>
                {importedContent.title && <p className="text-sm text-foreground font-medium">{importedContent.title}</p>}
                {importedContent.suggestedAngles?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-muted mb-1">Suggested angles:</p>
                    <ul className="text-xs text-muted space-y-1">
                      {importedContent.suggestedAngles.map((angle, i) => (
                        <li key={i}>• {angle}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={cn("w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium", s <= step ? "bg-primary text-white" : "bg-card-hover text-muted")}>
                  {s}
                </div>
                <span className={cn("text-sm hidden sm:inline", s <= step ? "text-foreground" : "text-muted")}>
                  {s === 1 ? "Basics" : s === 2 ? "Audience" : "Style"}
                </span>
                {s < 3 && <div className="flex-1 h-0.5 bg-border ml-2" />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
          {step === 1 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Let's start with the basics</h2>
                <p className="text-muted">Tell us about yourself and your niche.</p>
              </div>
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Your Name *</label>
                  <input 
                    type="text" 
                    value={formData.userName} 
                    onChange={(e) => {
                      setFormData({ ...formData, userName: e.target.value });
                      if (errors.userName) setErrors({ ...errors, userName: null });
                    }}
                    placeholder="e.g., Sarah Chen" 
                    className={cn("input w-full", errors.userName && "border-red-500")} 
                  />
                  {errors.userName && <p className="text-xs text-red-500 mt-1">{errors.userName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Your Niche *</label>
                  <input 
                    type="text" 
                    value={formData.niche} 
                    onChange={(e) => {
                      setFormData({ ...formData, niche: e.target.value });
                      if (errors.niche) setErrors({ ...errors, niche: null });
                    }}
                    placeholder="e.g., B2B SaaS Growth Strategy" 
                    className={cn("input w-full", errors.niche && "border-red-500")} 
                  />
                  {errors.niche && <p className="text-xs text-red-500 mt-1">{errors.niche}</p>}
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Who are you helping?</h2>
                <p className="text-muted">Define your ideal client and offer.</p>
              </div>
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Ideal Client *</label>
                  <input 
                    type="text" 
                    value={formData.targetAudience} 
                    onChange={(e) => {
                      setFormData({ ...formData, targetAudience: e.target.value });
                      if (errors.targetAudience) setErrors({ ...errors, targetAudience: null });
                    }}
                    placeholder="e.g., Series A founders struggling with product-market fit" 
                    className={cn("input w-full", errors.targetAudience && "border-red-500")} 
                  />
                  {errors.targetAudience && <p className="text-xs text-red-500 mt-1">{errors.targetAudience}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Core Offer *</label>
                  <textarea 
                    value={formData.offer} 
                    onChange={(e) => {
                      setFormData({ ...formData, offer: e.target.value });
                      if (errors.offer) setErrors({ ...errors, offer: null });
                    }}
                    placeholder="e.g., 12-week coaching program that helps founders validate their ideas and reach $1M ARR" 
                    className={cn("textarea w-full h-24", errors.offer && "border-red-500")} 
                  />
                  {errors.offer && <p className="text-xs text-red-500 mt-1">{errors.offer}</p>}
                </div>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Customize your style</h2>
                <p className="text-muted">Fine-tune tone and strategy.</p>
              </div>
              <div className="grid gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Tone</label>
                  <div className="flex flex-wrap gap-2">
                    {toneOptions.map((t) => (
                      <button key={t} onClick={() => setFormData({ ...formData, tone: t })} className={cn("px-4 py-2 rounded-full text-sm font-medium", formData.tone === t ? "bg-primary text-white" : "bg-card-hover text-muted hover:text-foreground")}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Goal</label>
                  <div className="flex flex-wrap gap-2">
                    {goalOptions.map((g) => (
                      <button key={g} onClick={() => setFormData({ ...formData, contentGoal: g })} className={cn("px-4 py-2 rounded-full text-sm font-medium", formData.contentGoal === g ? "bg-primary text-white" : "bg-card-hover text-muted hover:text-foreground")}>
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">Speed</label>
                  <div className="flex flex-wrap gap-2">
                    {speedOptions.map((s) => (
                      <button key={s} onClick={() => setFormData({ ...formData, funnelSpeed: s })} className={cn("px-4 py-2 rounded-full text-sm font-medium", formData.funnelSpeed === s ? "bg-primary text-white" : "bg-card-hover text-muted hover:text-foreground")}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Voice Examples (Optional)</label>
                  <textarea value={formData.voiceExamples} onChange={(e) => setFormData({ ...formData, voiceExamples: e.target.value })} placeholder="Paste your best posts here to help AI match your writing style..." className="textarea w-full h-32" />
                  <p className="text-xs text-muted mt-1">Optional: Add examples of your best content to help match your voice</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
          <button onClick={() => setStep(Math.max(1, step - 1))} className={cn("btn-secondary", step === 1 && "opacity-50")} disabled={step === 1}>
            Previous
          </button>
          {step < 3 ? (
            <button onClick={handleNext} disabled={!isStepValid()} className={cn("btn-primary", !isStepValid() && "opacity-50")}>
              Continue
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={loading} className="btn-primary flex items-center gap-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Sparkles className="w-4 h-4" /> Generate Sprint</>}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

export default CreateSprint;
